import React, { useEffect, useState, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { bookingsApi, documentsApi } from '../api/endpoints';
import BookingDrawer from '../components/BookingDrawer';
import { toast } from 'sonner';

const Registry = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [bookings, setBookings] = useState([]);
  const [docs, setDocs] = useState([]);
  const [types, setTypes] = useState({ registry: [] });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [activeBookingId, setActiveBookingId] = useState(null);
  const [registryStatus, setRegistryStatus] = useState({});
  
  // Router Driven Filter States
  const [applyStatusFilter, setApplyStatusFilter] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [bs, ds, ts] = await Promise.all([
        bookingsApi.list(),
        documentsApi.list({ category: 'Registry' }),
        documentsApi.types(),
      ]);
      setBookings(bs); 
      setDocs(ds); 
      setTypes(ts);
    } catch (e) { 
      toast.error('Failed to load registry data'); 
    } finally { 
      setLoading(false); 
    }
  };

  useEffect(() => { fetchAll(); }, []);

  // Intercept routing payload from Dashboard
  useEffect(() => {
    if (location.state?.registryStatusFilter) {
      setApplyStatusFilter(location.state.registryStatusFilter);
    } else {
      setApplyStatusFilter(null);
    }
  }, [location.state]);

  const requiredTypes = types.registry || [];

  // Memoized Filter & Search Logic
  const filteredBookings = useMemo(() => {
    const search = q.toLowerCase();
    return bookings.filter(b => {
      const matchesSearch = !search || (b.customer_name + (b.unit_no || '') + (b.project_name || '')).toLowerCase().includes(search);
      const matchesStatus = !applyStatusFilter || (registryStatus[b.id] || 'NOT DONE') === applyStatusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [bookings, q, applyStatusFilter, registryStatus]);

  // Efficient Document Lookup Map
  const docMap = useMemo(() => {
    const map = {};
    docs.forEach(d => { map[`${d.booking_id}-${d.doc_type}`] = d; });
    return map;
  }, [docs]);

  const handleRegistryUpdate = async (bookingId, status) => {
    try {
      setRegistryStatus(prev => ({ ...prev, [bookingId]: status }));
      toast.success(`Booking ${bookingId} marked as ${status}`);
    } catch (e) {
      toast.error('Failed to update registry status');
    }
  };

  const clearFilters = () => {
    setApplyStatusFilter(null);
    setQ('');
    navigate('.', { replace: true, state: null });
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="rounded-xl bg-gradient-to-r from-[#0F1F38] via-[#1B3A5C] to-[#2A4F7A] text-white p-5 lg:p-6 shadow-md">
        <div className="flex items-center gap-3 mb-2">
          <Icons.Gavel className="w-5 h-5 text-amber-400" />
          <div className="text-xs uppercase tracking-[0.25em] text-amber-400 font-bold">Legal Compliance</div>
        </div>
        <h2 className="text-2xl lg:text-3xl font-bold font-display">Registry Documents</h2>
        <p className="text-slate-300 text-sm mt-1 max-w-xl">
          Complete Sale Deed, Possession Letter, and Stamp Duty Calculation to enable Registry status.
        </p>
      </div>

      {/* Filter Banner */}
      {applyStatusFilter && (
        <div className="p-3 bg-blue-50 border border-blue-200 text-blue-800 rounded-lg flex items-center justify-between text-xs font-medium animate-in fade-in duration-300">
          <div className="flex items-center gap-2">
            <Icons.Filter className="w-4 h-4 text-blue-600" />
            <span>Showing records where Registry Status is: <strong>{applyStatusFilter}</strong></span>
          </div>
          <Button variant="ghost" size="sm" onClick={clearFilters} className="h-7 text-blue-700 hover:bg-blue-100 uppercase tracking-wider text-[10px]">
            Clear Filter
          </Button>
        </div>
      )}

      <Card className="border-slate-200">
        <CardContent className="p-3 sm:p-4">
          <div className="relative">
            <Icons.Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input className="pl-9" placeholder="Search customer, unit, or project..." value={q} onChange={e => setQ(e.target.value)} />
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="px-4 py-4 font-bold">Customer</th>
                <th className="px-4 py-4 hidden md:table-cell font-bold">Project / Unit</th>
                {requiredTypes.map(t => <th key={t} className="px-3 py-4 text-center font-bold">{t}</th>)}
                <th className="px-4 py-4 text-center font-bold">Registry Action</th>
                <th className="px-4 py-4"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <tr key={i}><td colSpan={10} className="p-4"><Skeleton className="h-10 w-full" /></td></tr>
                ))
              ) : filteredBookings.map(b => {
                let docsCount = 0;
                const docSlots = requiredTypes.map(t => {
                  const exists = docMap[`${b.id}-${t}`]?.file_url;
                  if (exists) docsCount++;
                  return (
                    <td key={t} className="px-3 py-3 text-center">
                      {exists 
                        ? <Icons.CheckCircle2 className="w-5 h-5 text-emerald-600 mx-auto" />
                        : <Icons.Circle className="w-5 h-5 text-slate-200 mx-auto" />
                      }
                    </td>
                  );
                });

                const isReady = docsCount === requiredTypes.length && requiredTypes.length > 0;
                const currentStatus = registryStatus[b.id] || 'NOT DONE';

                return (
                  <tr key={b.id} className="group hover:bg-slate-50/80 transition-colors">
                    <td className="px-4 py-3">
                      <div className="font-bold text-slate-800">{b.customer_name}</div>
                      <div className="text-[10px] text-slate-400 uppercase">ID: {b.id}</div>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      <div className="text-slate-700 font-medium">{b.project_name}</div>
                      <div className="text-[11px] text-slate-500 uppercase">Unit {b.unit_no}</div>
                    </td>
                    
                    {docSlots}

                    <td className="px-4 py-3 text-center">
                      {isReady ? (
                        <div className="flex flex-col items-center gap-1.5">
                          <select 
                            value={currentStatus}
                            onChange={(e) => handleRegistryUpdate(b.id, e.target.value)}
                            className={`text-[10px] font-black border rounded px-2 py-1.5 cursor-pointer outline-none transition-all ${
                              currentStatus === 'DONE' 
                              ? 'bg-emerald-600 text-white border-emerald-700 shadow-sm' 
                              : 'bg-white text-slate-600 border-slate-300'
                            }`}
                          >
                            <option value="NOT DONE">REGISTRY: NOT DONE</option>
                            <option value="DONE">REGISTRY: DONE</option>
                          </select>
                        </div>
                      ) : (
                        <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 font-bold">
                          {docsCount} / {requiredTypes.length} DOCS
                        </Badge>
                      )}
                    </td>
                    
                    <td className="px-4 py-3 text-right">
                      <button 
                        onClick={() => setActiveBookingId(b.id)}
                        className="p-2 hover:bg-white rounded-full transition-colors text-slate-400 hover:text-brand-navy"
                      >
                        <Icons.ExternalLink className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
              {!loading && filteredBookings.length === 0 && (
                <tr>
                  <td colSpan={10} className="text-center py-12 text-slate-400">
                    No registry records found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <BookingDrawer 
        bookingId={activeBookingId} 
        open={!!activeBookingId} 
        onOpenChange={(v) => !v && setActiveBookingId(null)} 
        onChanged={fetchAll} 
      />
    </div>
  );
};

export default Registry;